import React, { useEffect, useState } from 'react'
import { RouteComponentProps, Link, useHistory } from 'react-router-dom'
import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks'
import { PDFDownloadLink } from '@react-pdf/renderer'
import {
  Roles,
  VoucherApproval,
  VoucherApprovalStageStatus,
  VoucherCostCategory,
  VoucherState,
  VoucherType,
} from '../../types'
import { useAuthentication } from '../../hooks/useAuthentication'

import {
  Container,
  Grid,
  Grid2,
  Box,
  Button,
  IconButton,
  TextField,
  Card,
  CardContent,
  Paper,
  Toolbar,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import sv from 'date-fns/locale/sv'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'

import CostCategories from '../Vouchers/CostCategories'
import Loader from '../Loader'
import { APPROVE_VOUCHER, PAID_VOUCHER } from './gql'
import {
  GET_VOUCHER_BY_ID,
  UPDATE_VOUCHER,
  GET_CATEGORIES,
  VoucherUpdateInputMap,
  CREATE_VOUCHER_COST_CATEGORY,
  UPDATE_VOUCHER_COST_CATEGORY,
  DELETE_VOUCHER_COST_CATEGORY,
  GET_DEPARTMENTS,
  GET_USERS_LIST,
  GET_DOWNLOAD_LINK,
} from '../Vouchers/gql'
import PDFDoc from './PDFDoc'
import ApprovalStages from '../Common/ApprovalStages'
import { toast } from 'react-toastify'
import { getExpenseMonthOptions, isRoleAllowedPayments } from '../../utils/utils'
import { GET_NOTIFICATION_COUNT } from '../Header'
import { GET_INBOX } from './gql'

type VoucherProps = RouteComponentProps<{ id?: string }>
const Details: React.FunctionComponent<VoucherProps> = ({ match }) => {
  const { authState } = useAuthentication()

  const { loading, error, data } = useQuery(GET_VOUCHER_BY_ID, {
    variables: { id: match.params.id },
  })
  const departmentsResult = useQuery(GET_DEPARTMENTS)
  const usersResult = useQuery(GET_USERS_LIST)

  const categoriesResult = useQuery(GET_CATEGORIES)
  const [updateVoucher] = useMutation(UPDATE_VOUCHER)
  const [createVoucherCostCategory] = useMutation(CREATE_VOUCHER_COST_CATEGORY)
  const [updateVoucherCostCategory] = useMutation(UPDATE_VOUCHER_COST_CATEGORY)
  const [deleteVoucherCostCategory] = useMutation(DELETE_VOUCHER_COST_CATEGORY)

  const [approveVoucher] = useMutation(APPROVE_VOUCHER)
  const [paidVoucher] = useMutation(PAID_VOUCHER)
  // const [rejectVoucher, rejectVoucherResp] = useMutation(REJECT_VOUCHER)
  const history = useHistory()
  const today = new Date()
  const [voucher, setVoucher] = useState<VoucherType>({
    id: '',
    reference_number: 0,
    expense_period: null,
    description: '',
    state: '',
    total_amount: 0,
    voucher_cost_category: [] as VoucherCostCategory[],
    voucher_approvals: [] as VoucherApproval[],
    created_by: { id: '', first_name: '', last_name: '', email: '' },
    pay_to: '',
    claimed_by: { id: '', first_name: '', last_name: '', email: '' },
    department: { id: '', name: '' },
    paid_by_amin: null,
    payment_reference: '',
    receivers_account_number: '',
    created_at: new Date().getTime(),
    financial_year: today.getFullYear(),
    international_payment: false,
    currency: 'SEK',
    workflow: null,
    jamat: '',
    rejection_reason: '',
  })

  const isGenSec = (email?: string | null) =>
    !!email && /^gen\.sec_[^@]+@ahmadiyya\.se$/i.test(email)

  const allowedToEdit =
    (authState.authUser.role_id === Roles.NATIONAL.MAAL.id ||
      authState.authUser.role_id === Roles.BRANCH.MAAL.id ||
      authState.authUser.role === 'ADMIN' ||
      authState.authUser.role_id === Roles.BRANCH.SADR.id ||
      (voucher.created_by?.id === authState.authUser.id &&
        voucher.state === 'DRAFT'))

  const [categories, setCategories] = useState<Array<any>>([])

  const [departments, setDepartments] = React.useState<Array<any>>([])
  const [users, setUsers] = React.useState<Array<any>>([])
  const [selectedDepartment, setSelectedDepartment] = React.useState('')
  const [comment, setComment] = React.useState('')
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (
      isRoleAllowedPayments(authState.authUser.role_id) &&
      !voucher.paid_by_amin
    ) {
      setVoucher((prev) => ({
        ...prev,
        paid_by_amin: new Date().toISOString(),
      }))
    }
  }, [authState.authUser.role, voucher.id, voucher.paid_by_amin])

  // State for signed URLs
  const [signedUrls, setSignedUrls] = useState<{
    [key: string]: string | null
  }>({})

  // Helper function to check if file is an image
  const isImageFile = (fileName: string) => {
    if (!fileName) return false
    const extension = fileName.split('.').pop()?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
      extension || ''
    )
  }

  // Helper function to check if file is a PDF
  const isPDFFile = (fileName: string) => {
    if (!fileName) return false
    const extension = fileName.split('.').pop()?.toLowerCase()
    return extension === 'pdf'
  }

  // Lazy query for getting download links
  const [getFileDownloadLink] = useLazyQuery(GET_DOWNLOAD_LINK, {
    onCompleted: (downloadLinkData) => {
      const signedLink = downloadLinkData.signedDownloadLink.signedLink
      const receiptId = downloadLinkData.signedDownloadLink.receiptId

      if (receiptId) {
        setSignedUrls((prev) => ({
          ...prev,
          [receiptId]: signedLink,
        }))
      }
    },
  })

  // Fetch signed URLs for all image and PDF receipts
  useEffect(() => {
    if (voucher.voucher_cost_category) {
      voucher.voucher_cost_category.forEach((category) => {
        if (category.receipts) {
          category.receipts.forEach((receipt) => {
            if (
              (isImageFile(receipt.path) || isPDFFile(receipt.path)) &&
              !signedUrls[receipt.id]
            ) {
              setSignedUrls((prev) => ({
                ...prev,
                [receipt.id]: null, // Mark as loading
              }))
              getFileDownloadLink({
                variables: { id: receipt.id },
              })
            }
          })
        }
      })
    }
  }, [voucher.voucher_cost_category, getFileDownloadLink])

  useEffect(() => {
    if (loading === false && data) {
      let fetchedVoucher = { ...data.voucher }
      setVoucher(fetchedVoucher)
      setComment(fetchedVoucher.comment)
      setSelectedDepartment(fetchedVoucher.department.id)
    }
  }, [loading, data, authState.authUser.id])

  useEffect(() => {
    if (categoriesResult.loading === false && categoriesResult.data) {
      setCategories(categoriesResult.data.costCategories)
    }
  }, [categoriesResult.loading, categoriesResult.data])

  useEffect(() => {
    if (departmentsResult.loading === false && departmentsResult.data) {
      setDepartments(departmentsResult.data.departments)
    }
  }, [departmentsResult.loading, departmentsResult.data])

  useEffect(() => {
    if (usersResult.loading === false && usersResult.data) {
      setUsers(usersResult.data.users)
    }
  }, [usersResult.loading, usersResult.data])

  if (loading) return <Loader />
  if (error) return <p>ERROR: {error?.message}</p>

  // Cost Category Callback Functions
  const handleAddCostCategory = async (costCategory: any) => {
    console.log('handleAddCostCategory called with:', costCategory)
    console.log('voucher.id:', voucher.id)
    console.log('authState.authToken:', authState.authToken)
    try {
      console.log('Calling createVoucherCostCategory with:', {
        voucher_id: voucher.id,
        cost_category_id: costCategory.cost_category.id,
        cost_share: costCategory.cost_share,
      })
      const response = await createVoucherCostCategory({
        variables: {
          input: {
            voucher_id: voucher.id,
            cost_category_id: costCategory.cost_category.id,
            cost_share: costCategory.cost_share,
          },
        },
        context: { headers: { authorization: authState.authToken } },
      })
      console.log('GraphQL response:', response)

      if (response.data?.createVoucherCostCategory) {
        const newCostCategory = {
          id: response.data.createVoucherCostCategory.id,
          cost_category: costCategory.cost_category,
          cost_share: costCategory.cost_share,
          receipts: [],
        }

        setVoucher((prev) => ({
          ...prev,
          voucher_cost_category: [
            ...prev.voucher_cost_category,
            newCostCategory,
          ],
        }))
      }
    } catch (error) {
      console.error('Error adding cost category:', error)
      throw error
    }
  }

  const handleUpdateCostCategory = async (id: string, updates: any) => {
    console.log(
      'handleUpdateCostCategory called with id:',
      id,
      'updates:',
      updates
    )
    try {
      const response = await updateVoucherCostCategory({
        variables: {
          input: {
            id: id,
            cost_category_id: updates.cost_category.id,
            cost_share: updates.cost_share,
          },
        },
        context: { headers: { authorization: authState.authToken } },
      })

      if (response.data?.updateVoucherCostCategory) {
        setVoucher((prev) => ({
          ...prev,
          voucher_cost_category: prev.voucher_cost_category.map((cat) =>
            cat.id?.toString() === id
              ? {
                ...cat,
                cost_category: updates.cost_category,
                cost_share: updates.cost_share,
              }
              : cat
          ),
        }))
      }
    } catch (error) {
      console.error('Error updating cost category:', error)
      throw error
    }
  }

  const handleDeleteCostCategory = async (id: string) => {
    try {
      const response = await deleteVoucherCostCategory({
        variables: { id },
        context: { headers: { authorization: authState.authToken } },
      })

      if (response.data?.deleteVoucherCostCategory) {
        setVoucher((prev) => ({
          ...prev,
          voucher_cost_category: prev.voucher_cost_category.filter(
            (cat) => cat.id?.toString() !== id
          ),
        }))
      }
    } catch (error) {
      console.error('Error deleting cost category:', error)
      throw error
    }
  }

  console.log('Callback functions:', {
    handleAddCostCategory: typeof handleAddCostCategory,
    handleUpdateCostCategory: typeof handleUpdateCostCategory,
    handleDeleteCostCategory: typeof handleDeleteCostCategory,
  })

  const handleUpdate = async () => {
    try {
      const voucherInput = VoucherUpdateInputMap(voucher)
      if (selectedDepartment) {
        voucherInput.department = { connect: { id: selectedDepartment } }
      }

      const { data } = await updateVoucher({
        variables: { input: voucherInput },
      })

      if (data) {
        toast.success(
          `Voucher ${voucher.reference_number} updated successfully`
        )
        history.push('/panel/inbox')
      }
    } catch (err: any) {
      console.error('Error updating voucher:', err.message)
      toast.error(
        err.message || `Voucher ${voucher.reference_number} updated failed`
      )
    }
  }

  const handleApprove = async () => {
    setSubmitted(true);
    try {
      if (
        isRoleAllowedPayments(authState.authUser.role_id) &&
        voucher.state === VoucherState.APPROVED
      ) {
        if (!voucher.expense_period) return;
        const paidVoucherInput = {
          voucher_id: voucher.id,
          paid_by_amin: voucher.paid_by_amin,
          payment_reference: voucher.payment_reference,
          expense_period: voucher.expense_period,
        }
        const { data } = await paidVoucher({
          variables: { input: paidVoucherInput },
          refetchQueries: [
            { query: GET_NOTIFICATION_COUNT },
            { query: GET_INBOX },
          ],
          awaitRefetchQueries: true,
        })
        if (data) {
          toast.success(`Voucher ${voucher.reference_number} marked as paid`)
          history.push('/panel/inbox')
        }
      } else {
        const approveVoucherInput = {
          voucherId: voucher.id,
          status: VoucherApprovalStageStatus.APPROVED,
          comment: comment || null,
        }
        const { data } = await approveVoucher({
          variables: { input: approveVoucherInput },
          refetchQueries: [
            { query: GET_NOTIFICATION_COUNT },
            { query: GET_INBOX },
          ],
          awaitRefetchQueries: true,
        })
        if (data) {
          toast.success(
            `Voucher ${voucher.reference_number} approved successfully`
          )
          history.push('/panel/inbox')
        }
      }
    } catch (err: any) {
      console.error(err)
      toast.error(
        err.message || `Voucher ${voucher.reference_number} update failed`
      )
    }
  }

  const rejectionRoles = [
    'LOCAL_SEC_MALL',
    'LOCAL_PRESIDENT',
    'NATIONAL_SEC_MALL',
  ]

  // if (rejectVoucherResp.error) {
  //   console.error('Error Paid Voucher: ', rejectVoucherResp.error)
  // }

  // if (rejectVoucherResp.loading === false && rejectVoucherResp.data) {
  //   return <Redirect to="/panel/inbox" />
  // }

  const pdfVoucher = {
    id: voucher.id,
    reference_number: voucher.reference_number,
    description: voucher.description,
    state: voucher.state,
    created_by: voucher.created_by,
    pay_to: voucher.pay_to,
    department: voucher.department,
    created_at: voucher.created_at,
    financial_year: voucher.financial_year,
    paid_by_amin: voucher.paid_by_amin,
    payment_reference: voucher.payment_reference,
    voucher_cost_category: voucher.voucher_cost_category,
    categories: voucher.voucher_cost_category.map((c: any) => ({
      code: c.cost_category?.code,
      category: c.cost_category?.parent_id
        ? categories.find((cat: any) => cat.id === c.cost_category?.parent_id)
          ?.name
        : 'N/A',
      sub_category: c.cost_category?.name,
      cost_share: c.cost_share,
    })),
    signedUrls: signedUrls, // Pass signed URLs to PDF component
  }

  return (
    <Paper square>
      <Toolbar
        sx={{
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          borderBottom: '1px solid',
          borderColor: 'rgba(25, 118, 210, 0.2)',
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: 500,
            letterSpacing: '0.3px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            color: 'text.secondary',
          }}
        >
          <Box
            component="span"
            sx={{
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              color: 'primary.main',
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              fontSize: '0.9em',
              fontWeight: 500,
              border: '1px solid',
              borderColor: 'rgba(25, 118, 210, 0.2)',
            }}
          >
            #{voucher.reference_number}
          </Box>
          <Box component="span" sx={{ opacity: 0.6, color: 'text.secondary' }}>
            -
          </Box>
          <Box
            component="span"
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              color: 'success.main',
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '1.1em',
              border: '1px solid',
              borderColor: 'rgba(76, 175, 80, 0.3)',
            }}
          >
            {voucher.total_amount} SEK
          </Box>
        </Typography>
        <Box flexGrow="1" />
        <Box gap={1} display="flex" alignItems="center">
          <Button
            size="small"
            aria-label="Back"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/panel/inbox"
          >
            Back
          </Button>
        </Box>
      </Toolbar>
      <Container>
        <div style={{ paddingTop: '32px' }}>
          {/* Description Row - Full Width */}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                size="small"
                label="Description"
                value={voucher.description}
                variant="outlined"
                onChange={(e: any) =>
                  setVoucher({ ...voucher, description: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {/* First Row of Fields */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Reference Number"
                value={voucher.reference_number}
                variant="outlined"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="State"
                value={voucher.state}
                variant="outlined"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Created By"
                value={
                  voucher.created_by?.first_name +
                  ' ' +
                  voucher.created_by?.last_name
                }
                variant="outlined"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Created at"
                value={
                  new Date(voucher.created_at * 1).toISOString().split('T')[0]
                }
                variant="outlined"
                disabled
              />
            </Grid>
          </Grid>

          {/* Second Row of Fields */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Pay to"
                value={voucher.pay_to}
                variant="outlined"
                disabled={!allowedToEdit}
                onChange={(e: any) =>
                  setVoucher({ ...voucher, pay_to: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="voucehr-financial-year-label">
                  Financial year
                </InputLabel>
                <Select
                  id="voucehr-financial-year"
                  value={voucher.financial_year}
                  label="Financial year"
                  disabled={!allowedToEdit}
                  onChange={(e: any) =>
                    setVoucher({ ...voucher, financial_year: e.target.value })
                  }
                >
                  {Array(
                    voucher.financial_year + 1 - (voucher.financial_year - 3)
                  )
                    .fill('')
                    .map((v, idx) => (
                      <MenuItem
                        key={`financial-year-${idx}`}
                        value={voucher.financial_year + 1 - idx}
                      >{`${voucher.financial_year - idx}-${voucher.financial_year - idx + 1
                        }`}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="voucehr-department-label">
                  Department
                </InputLabel>
                <Select
                  labelId="department-select-label"
                  id="department-select"
                  value={voucher.department.id}
                  label="Department"
                  disabled={!allowedToEdit}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  {departments.map((d: any) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <ApprovalStages voucher={voucher} />
          <CostCategories
            voucher={voucher}
            readonly={!allowedToEdit || voucher.state === VoucherState.PAID}
            onAdd={handleAddCostCategory}
            onUpdate={handleUpdateCostCategory}
            onDelete={handleDeleteCostCategory}
          />

          <Grid2 container spacing={2} alignItems="end" marginTop={3}>
            <Grid2 size={12}>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="baseline"
                marginBottom={2}
              >
                {(((authState.authUser.role_id === Roles.BRANCH.MAAL.id || authState.authUser.role_id === Roles.BRANCH.SADR.id) &&
                  (voucher.state !== VoucherState.APPROVED && voucher.state !== VoucherState.PAID)) ||
                  (authState.authUser.role_id === Roles.NATIONAL.MAAL.id && voucher.state !== VoucherState.PAID)) && (
                    <Button
                      variant="contained"
                      color="inherit"
                      onClick={handleUpdate}
                      className="update-btn"
                    >
                      Update
                    </Button>
                  )}
              </Box>
            </Grid2>
          </Grid2>

          {voucher &&
            voucher.workflow &&
            voucher.state !== VoucherState.PAID &&
            voucher.voucher_approvals.find(
              (v) => v.approver.id === authState.authUser.id
            )?.status === VoucherApprovalStageStatus.PENDING && (
              <>
                <Box marginTop={2}>
                  <hr />
                </Box>
                <Box marginTop={2}>
                  <Grid container>
                    <Grid item sm={6}>
                      {isRoleAllowedPayments(authState.authUser.role_id) &&
                        voucher.state === VoucherState.APPROVED ? (
                        <>
                          <Box display="flex" gap={2} mb={2}>
                            <LocalizationProvider
                              dateAdapter={AdapterDateFns}
                              adapterLocale={sv}
                            >
                              <DatePicker
                                label="Payment Date"
                                value={
                                  voucher.paid_by_amin
                                    ? new Date(voucher.paid_by_amin)
                                    : new Date()
                                }
                                onChange={(newValue: Date | null) => {
                                  if (newValue) {
                                    setVoucher({
                                      ...voucher,
                                      paid_by_amin:
                                        newValue.toISOString() ||
                                        new Date().toISOString(),
                                    })
                                  }
                                }}
                              />
                            </LocalizationProvider>
                            <FormControl size="small" sx={{ minWidth: 150 }} required error={submitted && !voucher.expense_period}>
                              <InputLabel id="expense-month-label">Expense month</InputLabel>
                              <Select
                                labelId="expense-month-label"
                                label="Expense month"
                                value={voucher.expense_period || ''}
                                onChange={(e) =>
                                  setVoucher({
                                    ...voucher,
                                    expense_period: e.target.value ? (e.target.value as number) : null,
                                  })
                                }
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {getExpenseMonthOptions().map((opt) => (
                                  <MenuItem key={opt.key} value={opt.key}>
                                    {opt.value}
                                  </MenuItem>
                                ))}
                              </Select>
                              {submitted && !voucher.expense_period && (
                                <FormHelperText>Expense month is required</FormHelperText>
                              )}
                            </FormControl>
                          </Box>
                          <Card>
                            <CardContent>
                              <TextField
                                fullWidth
                                multiline
                                rows={4}
                                size="small"
                                label="Payment reference"
                                value={voucher.payment_reference}
                                margin="dense"
                                variant="outlined"
                                onChange={(event) =>
                                  setVoucher({
                                    ...voucher,
                                    payment_reference:
                                      event.currentTarget.value,
                                  })
                                }
                              />
                            </CardContent>
                          </Card>
                        </>
                      ) : (
                        <Card>
                          <CardContent>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              size="small"
                              label="Reason/Comments"
                              value={comment}
                              margin="dense"
                              variant="outlined"
                              onChange={(event) =>
                                setComment(event.currentTarget.value)
                              }
                            />
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                  </Grid>
                </Box>
                <Box display="flex" justifyContent="flex-end" py={2}>
                  <Box px={4}></Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApprove}
                  >
                    {isRoleAllowedPayments(authState.authUser.role_id) &&
                      voucher.state === VoucherState.APPROVED
                      ? 'Mark as Paid'
                      : 'Approve'}
                  </Button>
                </Box>
              </>
            )}
        </div>
      </Container>
    </Paper>
  )
}

export default Details
